import * as React from 'react';
import {
    Alert, Box, Button, Grid, Snackbar, Stack, TextField, ThemeProvider, Typography
} from '@mui/material';
import { generateTheme } from '../themes';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { detectLang, DEFAULT_LANG } from '../utils/language';
import { detectMarket, buildMarketPath } from '../utils/market';

const CheckIcon = ({ size = 22, color = '#0091A5' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const CONTACT_ICONS = {
    mail: (
        <g>
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22 6 12 13 2 6" />
        </g>
    ),
    phone: (
        <g>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </g>
    ),
    calendar: (
        <g>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
        </g>
    ),
    pin: (
        <g>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
        </g>
    ),
    globe: (
        <g>
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </g>
    )
};

const ContactIcon = ({ name, size = 18 }) => {
    const content = CONTACT_ICONS[name];
    if (!content) return null;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {content}
        </svg>
    );
};

const fieldSx = {
    backgroundColor: '#fff',
    borderRadius: '10px',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        fontSize: '0.95rem',
        '& > fieldset': { borderColor: '#E2DDD5' },
        '&:hover > fieldset': { borderColor: '#C8C0B3' },
        '&.Mui-focused > fieldset': { borderColor: '#5C33D7', borderWidth: 1.5 }
    }
};

const fieldLabelSx = {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'text.primary',
    mb: 0.75,
    display: 'block'
};

const FORM_LABELS = {
    sv: {
        firstName: 'Förnamn', firstNamePlaceholder: 'Anna',
        lastName: 'Efternamn',  lastNamePlaceholder: 'Andersson',
        email: 'E-postadress',  emailPlaceholder: 'anna@företaget.se',
        phone: 'Telefonnummer', phonePlaceholder: '070-123 45 67',
        business: 'Företag eller organisation', businessPlaceholder: 'Företagets namn',
        message: 'Meddelande',  messagePlaceholder: 'Berätta gärna lite om era behov eller vad ni är nyfikna på.',
        messageHint: 'Frivilligt — hjälper oss att förbereda mötet bättre.'
    },
    en: {
        firstName: 'First name', firstNamePlaceholder: 'Anna',
        lastName: 'Last name',   lastNamePlaceholder: 'Andersson',
        email: 'Email address',  emailPlaceholder: 'anna@company.com',
        phone: 'Phone number',   phonePlaceholder: '+46 70 123 45 67',
        business: 'Company or organisation', businessPlaceholder: 'Company name',
        message: 'Message',      messagePlaceholder: 'Tell us a bit about your needs or what you’d like to know.',
        messageHint: 'Optional — helps us prepare the call better.'
    }
};

const ContactSlice = ({ data }) => {
    const isEnglish = typeof window !== 'undefined' && window.location.pathname.startsWith('/en/');
    const labels = isEnglish ? FORM_LABELS.en : FORM_LABELS.sv;

    const {
        colorTheme = 'off-white',
        eyebrow = '',
        title = '',
        description = '',
        benefits = [],
        infoTag = '',
        directContactHeading = '',
        directContacts = [],
        customersHeading = '',
        customers = [],
        formHeading = '',
        formSubheading = '',
        submitLabel = isEnglish ? 'Send and get a reply' : 'Skicka',
        privacyNote = '',
        successMessage: successMessageText = isEnglish ? 'Thanks for your message!' : 'Tack för ditt meddelande!',
        errorMessage: errorMessageText = isEnglish ? 'Something went wrong, please try again!' : 'Någonting gick fel, försök igen!'
    } = data || {};

    const theme = generateTheme(colorTheme);

    const [formValues, setFormValues] = useState({
        firstname: '', lastname: '', email: '', phone: '', business: '', message: ''
    });
    const [formPosted, setFormPosted] = useState(false);
    const [formError, setFormError] = useState(false);

    const containerRef = useRef(null);
    const recaptchaLoaded = useRef(false);

    const loadRecaptcha = () => {
        if (recaptchaLoaded.current) return;
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=6LccvMQjAAAAAI7wLF3R_hrVj9ayNKwFGCPm06rw';
        script.async = true;
        document.body.appendChild(script);
        recaptchaLoaded.current = true;
    };

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const targets = gsap.utils.toArray('.cs-animate', containerRef.current);
        targets.forEach((el, i) => {
            gsap.fromTo(el,
                { opacity: 0, y: 10 },
                {
                    opacity: 1, y: 0, duration: 0.7, delay: 0.06 * i, ease: 'easeIn',
                    scrollTrigger: { trigger: el, start: 'top 88%', markers: false }
                }
            );
        });
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues(prev => ({ ...prev, [name]: value }));
    };

    const sendGenerateLeadEvent = ({ email, firstname, lastname }) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'generate_lead',
            user_data: { email: email || '', address: { first_name: firstname || '', last_name: lastname || '' } },
            contact_reason: 'other'
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);

        if (!window.grecaptcha) {
            setFormError(true);
            return;
        }

        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute('6LccvMQjAAAAAI7wLF3R_hrVj9ayNKwFGCPm06rw', { action: 'submit' })
                .then(async (token) => {
                    try {
                        const market = detectMarket(window.location.pathname);
                        form.append('token', token);
                        form.append('lang', market.lang);
                        form.append('country', market.country);
                        form.append('currency', market.currency);
                        form.append('dept', 'sales');
                        const response = await fetch('https://ccmail.cardcam.io/contact_lang.php', {
                            method: 'post',
                            body: form
                        });
                        if (response.status === 200) {
                            const text = await response.text();
                            try {
                                if (JSON.parse(text).status) {
                                    sendGenerateLeadEvent({
                                        email: formValues.email,
                                        firstname: formValues.firstname,
                                        lastname: formValues.lastname
                                    });
                                    const thankYouUrl = buildMarketPath(market, 'thank-you');
                                    setTimeout(() => { window.location.href = thankYouUrl; }, 300);
                                    return;
                                }
                            } catch (e) { /* fall through */ }
                        }
                        setFormError(true);
                        setFormPosted(false);
                    } catch (error) {
                        setFormError(true);
                        setFormPosted(false);
                    }
                });
        });
    };

    const handleSnackbarClose = (_event, reason) => {
        if (reason === 'clickaway') return;
        setFormError(false);
    };

    return (
        <ThemeProvider theme={theme}>
            <Box
                ref={containerRef}
                id="contact"
                sx={{
                    background: (t) => t.palette.background.default,
                    px: { xs: 3, md: 7.5 },
                    pt: { xs: 5, md: 5 },
                    pb: { xs: 8, md: 10 }
                }}
            >
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Centered top header */}
                    {(eyebrow || title) && (
                        <Box sx={{ textAlign: 'center', mb: { xs: 7, md: 11 } }}>
                            {eyebrow && (
                                <Typography
                                    className="cs-animate"
                                    component="span"
                                    sx={{
                                        display: 'block',
                                        color: 'text.secondary',
                                        letterSpacing: '0.2em',
                                        textTransform: 'uppercase',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        opacity: 0.7,
                                        mb: 1
                                    }}
                                    dangerouslySetInnerHTML={{ __html: eyebrow }}
                                />
                            )}
                            {eyebrow && (
                                <Box
                                    aria-hidden="true"
                                    className="cs-animate"
                                    sx={{
                                        height: '4px',
                                        width: 160,
                                        borderRadius: '2px',
                                        mx: 'auto',
                                        mb: { xs: 2.5, md: 3 },
                                        background: 'linear-gradient(90deg, rgba(92, 51, 215, 0) 0%, rgb(92, 51, 215) 100%)'
                                    }}
                                />
                            )}
                            {title && (
                                <Typography
                                    className="cs-animate"
                                    component="h2"
                                    sx={{
                                        fontFamily: 'Kanit, sans-serif',
                                        fontWeight: 900,
                                        fontSize: { xs: '40px', md: '64px' },
                                        lineHeight: 0.95,
                                        letterSpacing: '-1.5px',
                                        textTransform: 'uppercase',
                                        color: 'text.primary',
                                        m: 0
                                    }}
                                    dangerouslySetInnerHTML={{ __html: title }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Two-column layout */}
                    <Grid container spacing={{ xs: 5, md: 7 }} alignItems="flex-start">
                        {/* LEFT: Intro content */}
                        <Grid item xs={12} md={6}>
                            <Stack spacing={{ xs: 4, md: 5 }}>
                                {description && (
                                    <Typography
                                        className="cs-animate"
                                        component="p"
                                        sx={{
                                            fontSize: { xs: '1rem', md: '1.0625rem' },
                                            color: 'text.primary',
                                            lineHeight: 1.6,
                                            m: 0
                                        }}
                                        dangerouslySetInnerHTML={{ __html: description }}
                                    />
                                )}

                                {benefits?.length > 0 && (
                                    <Box
                                        component="ul"
                                        sx={{
                                            listStyle: 'none',
                                            p: 0,
                                            m: 0
                                        }}
                                    >
                                        {benefits.map((b, i) => (
                                            <Box
                                                key={i}
                                                component="li"
                                                className="cs-animate"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 1.25,
                                                    py: 1
                                                }}
                                            >
                                                <Box sx={{ flexShrink: 0, mt: '2px' }}>
                                                    <CheckIcon size={18} />
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    {b.title && (
                                                        <Typography
                                                            component="h3"
                                                            sx={{
                                                                fontSize: '0.9375rem',
                                                                fontWeight: 600,
                                                                color: 'text.primary',
                                                                lineHeight: 1.3,
                                                                m: 0,
                                                                mb: b.description ? 0.25 : 0
                                                            }}
                                                            dangerouslySetInnerHTML={{ __html: b.title }}
                                                        />
                                                    )}
                                                    {b.description && (
                                                        <Typography
                                                            component="p"
                                                            sx={{
                                                                fontSize: '0.875rem',
                                                                color: 'text.secondary',
                                                                lineHeight: 1.4,
                                                                m: 0
                                                            }}
                                                            dangerouslySetInnerHTML={{ __html: b.description }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {infoTag && (
                                    <Box
                                        className="cs-animate"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1.25,
                                            alignSelf: 'flex-start',
                                            background: '#DCEDF1',
                                            color: '#16121C',
                                            borderRadius: '999px',
                                            px: 2,
                                            py: 1,
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <Box
                                            aria-hidden="true"
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                background: '#0091A5',
                                                flexShrink: 0
                                            }}
                                        />
                                        <span dangerouslySetInnerHTML={{ __html: infoTag }} />
                                    </Box>
                                )}

                                {(directContactHeading || directContacts?.length > 0) && (
                                    <Box className="cs-animate">
                                        {directContactHeading && (
                                            <Typography
                                                component="h3"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.18em',
                                                    textTransform: 'uppercase',
                                                    color: 'text.secondary',
                                                    opacity: 0.7,
                                                    m: 0,
                                                    mb: 2
                                                }}
                                                dangerouslySetInnerHTML={{ __html: directContactHeading }}
                                            />
                                        )}
                                        <Stack spacing={1.5}>
                                            {directContacts.map((c, i) => {
                                                const isLink = !!c.href;
                                                const inner = (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1.5,
                                                            color: '#5C33D7'
                                                        }}
                                                    >
                                                        <Box sx={{ flexShrink: 0, display: 'flex' }}>
                                                            <ContactIcon name={c.icon || 'mail'} size={18} />
                                                        </Box>
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                fontSize: '0.9375rem',
                                                                color: '#5C33D7',
                                                                fontWeight: 500
                                                            }}
                                                            dangerouslySetInnerHTML={{ __html: c.text || '' }}
                                                        />
                                                    </Box>
                                                );
                                                return isLink ? (
                                                    <a
                                                        key={i}
                                                        href={c.href}
                                                        style={{ textDecoration: 'none' }}
                                                    >
                                                        {inner}
                                                    </a>
                                                ) : (
                                                    <Box key={i}>{inner}</Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                )}

                                {(customersHeading || customers?.length > 0) && (
                                    <Box className="cs-animate" sx={{ pt: { xs: 1, md: 2 } }}>
                                        {customersHeading && (
                                            <Typography
                                                component="h3"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.18em',
                                                    textTransform: 'uppercase',
                                                    color: 'text.secondary',
                                                    opacity: 0.6,
                                                    m: 0,
                                                    mb: 2
                                                }}
                                                dangerouslySetInnerHTML={{ __html: customersHeading }}
                                            />
                                        )}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                alignItems: 'center',
                                                gap: { xs: 3, md: 4 }
                                            }}
                                        >
                                            {customers.map((c, i) => (
                                                c.logoUrl ? (
                                                    <Box
                                                        key={i}
                                                        component="img"
                                                        src={c.logoUrl}
                                                        alt={c.logoAlt || c.name || ''}
                                                        sx={{
                                                            height: { xs: 22, md: 26 },
                                                            width: 'auto',
                                                            objectFit: 'contain',
                                                            display: 'block',
                                                            filter: 'grayscale(1)',
                                                            opacity: 0.7,
                                                            transition: 'filter 0.3s ease, opacity 0.3s ease',
                                                            '&:hover': {
                                                                filter: 'grayscale(0)',
                                                                opacity: 1
                                                            }
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography
                                                        key={i}
                                                        component="span"
                                                        sx={{
                                                            fontFamily: 'Kanit, sans-serif',
                                                            fontWeight: 800,
                                                            letterSpacing: '0.06em',
                                                            fontSize: { xs: '0.875rem', md: '0.9375rem' },
                                                            color: 'text.secondary'
                                                        }}
                                                    >
                                                        {c.name || ''}
                                                    </Typography>
                                                )
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Stack>
                        </Grid>

                        {/* RIGHT: Form card */}
                        <Grid item xs={12} md={6}>
                            <Box
                                className="cs-animate"
                                sx={{
                                    background: '#fff',
                                    borderRadius: { xs: '20px', md: '24px' },
                                    p: { xs: '24px', md: '36px' },
                                    border: '0.5px solid #E8E4DF',
                                    boxShadow: '0 4px 24px rgba(22, 18, 28, 0.06)'
                                }}
                            >
                                {formHeading && (
                                    <Typography
                                        component="h2"
                                        sx={{
                                            fontFamily: 'Kanit, sans-serif',
                                            fontWeight: 800,
                                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                                            color: 'text.primary',
                                            lineHeight: 1.15,
                                            letterSpacing: '-0.5px',
                                            m: 0,
                                            mb: formSubheading ? 1 : 3
                                        }}
                                        dangerouslySetInnerHTML={{ __html: formHeading }}
                                    />
                                )}
                                {formSubheading && (
                                    <Typography
                                        component="p"
                                        sx={{
                                            fontSize: '0.9375rem',
                                            color: 'text.secondary',
                                            lineHeight: 1.5,
                                            m: 0,
                                            mb: 3
                                        }}
                                        dangerouslySetInnerHTML={{ __html: formSubheading }}
                                    />
                                )}

                                <Box
                                    component="form"
                                    onSubmit={handleSubmit}
                                    onFocus={loadRecaptcha}
                                    sx={{ pt: 1 }}
                                >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography component="label" htmlFor="firstname-input" sx={fieldLabelSx}>
                                                {labels.firstName} <span style={{ color: '#5C33D7' }}>*</span>
                                            </Typography>
                                            <TextField
                                                id="firstname-input"
                                                name="firstname"
                                                fullWidth
                                                required
                                                size="small"
                                                placeholder={labels.firstNamePlaceholder}
                                                value={formValues.firstname}
                                                onChange={handleInputChange}
                                                sx={fieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography component="label" htmlFor="lastname-input" sx={fieldLabelSx}>
                                                {labels.lastName} <span style={{ color: '#5C33D7' }}>*</span>
                                            </Typography>
                                            <TextField
                                                id="lastname-input"
                                                name="lastname"
                                                fullWidth
                                                required
                                                size="small"
                                                placeholder={labels.lastNamePlaceholder}
                                                value={formValues.lastname}
                                                onChange={handleInputChange}
                                                sx={fieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography component="label" htmlFor="email-input" sx={fieldLabelSx}>
                                                {labels.email} <span style={{ color: '#5C33D7' }}>*</span>
                                            </Typography>
                                            <TextField
                                                id="email-input"
                                                name="email"
                                                type="email"
                                                autoComplete="email"
                                                fullWidth
                                                required
                                                size="small"
                                                placeholder={labels.emailPlaceholder}
                                                value={formValues.email}
                                                onChange={handleInputChange}
                                                sx={fieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography component="label" htmlFor="phone-input" sx={fieldLabelSx}>
                                                {labels.phone}
                                            </Typography>
                                            <TextField
                                                id="phone-input"
                                                name="phone"
                                                type="tel"
                                                autoComplete="tel"
                                                fullWidth
                                                size="small"
                                                placeholder={labels.phonePlaceholder}
                                                value={formValues.phone}
                                                onChange={handleInputChange}
                                                sx={fieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography component="label" htmlFor="business-input" sx={fieldLabelSx}>
                                                {labels.business} <span style={{ color: '#5C33D7' }}>*</span>
                                            </Typography>
                                            <TextField
                                                id="business-input"
                                                name="business"
                                                fullWidth
                                                required
                                                size="small"
                                                placeholder={labels.businessPlaceholder}
                                                value={formValues.business}
                                                onChange={handleInputChange}
                                                sx={fieldSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography component="label" htmlFor="message-input" sx={fieldLabelSx}>
                                                {labels.message}
                                            </Typography>
                                            <TextField
                                                id="message-input"
                                                name="message"
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                size="small"
                                                placeholder={labels.messagePlaceholder}
                                                value={formValues.message}
                                                onChange={handleInputChange}
                                                sx={fieldSx}
                                            />
                                            <Typography
                                                sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.75 }}
                                            >
                                                {labels.messageHint}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Button
                                        type="submit"
                                        fullWidth
                                        disableElevation
                                        sx={{
                                            mt: 3,
                                            py: 1.5,
                                            backgroundColor: '#5C33D7',
                                            color: '#fff',
                                            borderRadius: '10px',
                                            fontSize: '0.9375rem',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontFamily: 'Roboto, sans-serif',
                                            '&:hover': { backgroundColor: '#4C1D95' }
                                        }}
                                    >
                                        {submitLabel}
                                    </Button>

                                    {privacyNote && (
                                        <Typography
                                            component="p"
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: 'text.secondary',
                                                textAlign: 'center',
                                                lineHeight: 1.5,
                                                mt: 2,
                                                m: 0,
                                                '& a': { color: 'text.secondary', textDecoration: 'underline' }
                                            }}
                                            dangerouslySetInnerHTML={{ __html: privacyNote }}
                                        />
                                    )}

                                    <Snackbar open={formError} autoHideDuration={6000} onClose={handleSnackbarClose}>
                                        <Alert onClose={handleSnackbarClose} variant="filled" severity="error" sx={{ width: '100%' }}>
                                            {errorMessageText}
                                        </Alert>
                                    </Snackbar>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default ContactSlice;
